import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiClock, FiMapPin, FiPhone, FiUsers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { tourService } from '../services/tourService';
import { ErrorState, LoadingState, PageHero, SectionHeading } from '../components/ui/PageElements';
import { formatCurrency } from '../utils/formatters';

const TourDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTour = async () => {
      try {
        setLoading(true);
        const data = await tourService.getTourById(id);
        setTour(data);
        setError('');
      } catch (requestError) {
        console.error('Tour detail error:', requestError);
        setError(t('design.tourDetail.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchTour();
  }, [id, t]);

  if (loading) {
    return (
      <Layout>
        <LoadingState label={t('design.tourDetail.loading')} />
      </Layout>
    );
  }

  if (error || !tour) {
    return (
      <Layout>
        <ErrorState title={t('design.tourDetail.unavailableTitle')} message={error || t('design.tourDetail.unavailableMessage')} action={<Link to="/tours" className="btn-primary">{t('design.tourDetail.backToTours')}</Link>} />
      </Layout>
    );
  }

  return (
    <Layout>
      <PageHero
        eyebrow={t('design.tourDetail.heroEyebrow')}
        title={tour.tourName}
        description={t('design.tourDetail.heroDescription')}
        actions={
          <>
            <Link to={`/tour-reservation/${tour.id}`} className="btn-primary">{t('design.tourDetail.reserve')}</Link>
            {tour.phone ? (
              <a href={`tel:${tour.phone}`} className="btn-ghost">
                <FiPhone className="h-4 w-4" />
                {t('design.tourDetail.callOrganizer')}
              </a>
            ) : null}
          </>
        }
        aside={
          <div className="soft-panel overflow-hidden">
            <div className="relative h-[360px]">
              <img
                src={tour.photoUrl || '/tour-placeholder.jpg'}
                alt={tour.tourName}
                className="h-full w-full object-cover"
                onError={(event) => {
                  event.currentTarget.src = '/tour-placeholder.jpg';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-950/72 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-xs uppercase tracking-[0.2em] text-white/70">{t('design.tourDetail.perGuestPrice')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold">{formatCurrency(tour.price)}</p>
                <p className="text-sm text-white/80">{t('tours.perPerson')}</p>
              </div>
            </div>
          </div>
        }
      />

      <section className="content-width pb-12">
        <div className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
          <div className="soft-panel p-6 sm:p-8">
            <SectionHeading
              eyebrow={t('design.tourDetail.summaryEyebrow')}
              title={t('design.tourDetail.summaryTitle')}
              description={t('design.tourDetail.summaryDescription')}
            />
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourDetail.start')}</p>
                <p className="mt-3 flex items-center gap-2 text-slate-700">
                  <FiMapPin className="text-teal-700" />
                  {tour.tourStartAddress}
                </p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourDetail.end')}</p>
                <p className="mt-3 flex items-center gap-2 text-slate-700">
                  <FiMapPin className="text-teal-700" />
                  {tour.tourEndAddress}
                </p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('tours.duration')}</p>
                <p className="mt-3 flex items-center gap-2 text-slate-700">
                  <FiClock className="text-teal-700" />
                  {t('design.tourDetail.durationValue', { count: tour.duration || 1 })}
                </p>
              </div>
              <div className="rounded-[24px] border border-stone-200 bg-stone-50/80 p-5">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('tours.groupSize')}</p>
                <p className="mt-3 flex items-center gap-2 text-slate-700">
                  <FiUsers className="text-teal-700" />
                  {t('design.tourDetail.participantsValue', { count: tour.participantCount || 0 })}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('hotels.reservation.title')}</p>
              <div className="mt-5 rounded-[28px] border border-stone-200 bg-stone-50/80 p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500">{t('design.tourDetail.perGuest')}</p>
                <p className="mt-2 font-serif text-4xl font-semibold text-slate-900">{formatCurrency(tour.price)}</p>
                <p className="mt-3 text-sm leading-7 text-slate-600">{t('design.tourDetail.reservationHint')}</p>
                <Link to={`/tour-reservation/${tour.id}`} className="btn-primary mt-6 w-full justify-center">{t('design.tourDetail.reserveThisTour')}</Link>
              </div>
            </div>

            <div className="soft-panel p-6 sm:p-8">
              <p className="eyebrow">{t('places.contact')}</p>
              <p className="mt-4 text-sm leading-7 text-slate-600">{tour.phone || t('design.tourDetail.contactFallback')}</p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TourDetailPage;
